<template>
  <el-dialog
    class="dialog-wrapper"
    :visible="visible"
    :show-close="false"
    @close="abortUpdate"
  >
    <ContentHeader
      slot="title"
      :title="$t('users.update.updateUser')"
    />
    <ValidationObserver
      ref="userForm"
      v-slot="{ handleSubmit, invalid }"
    >
      <el-form
        ref="form"
        label-position="top"
      >
        <el-row
          :gutter="20"
          type="flex"
        >
          <el-col :span="12">
            <FormItem
              rules="required"
              :label="$t('users.create.username')"
            >
              <el-input v-model="formData.username" />
            </FormItem>
          </el-col>
          <el-col :span="12">
            <FormItem :label="$t('users.create.email')">
              <el-input v-model="formData.email" />
            </FormItem>
          </el-col>
        </el-row>

        <el-row
          :gutter="20"
          type="flex"
        >
          <el-col :span="12">
            <FormItem :label="$t('users.create.givenName')">
              <el-input v-model="formData.givenName" />
            </FormItem>
          </el-col>
          <el-col :span="12">
            <FormItem :label="$t('users.create.surname')">
              <el-input v-model="formData.surname" />
            </FormItem>
          </el-col>
        </el-row>

        <el-row
          :gutter="20"
          type="flex"
        >
          <el-col :span="6">
            <FormItem :label="$t('users.create.laboratory')">
              <el-select
                v-model="formData.laboratory"
                placeholder=""
              >
                <el-option
                  v-for="laboratory in laboratories"
                  :key="laboratory"
                  :value="laboratory"
                  :label="$t(`constants.${laboratory}`)"
                />
              </el-select>
            </FormItem>
          </el-col>
          <el-col :span="6">
            <FormItem :label="$t('users.create.role')">
              <el-select
                v-model="formData.role"
                placeholder=""
              >
                <el-option
                  v-for="role in roles"
                  :key="role"
                  :value="role"
                  :label="role"
                />
              </el-select>
            </FormItem>
          </el-col>
          <el-col :span="6">
            <FormItem :label="$t('users.create.location')">
              <el-autocomplete
                v-model="formData.locationName"
                style="width: 100%"
                :fetch-suggestions="locationsList"
                :trigger-on-focus="false"
                value-key="label"
                clearable
                placeholder=""
                @select="
                  (item) => {
                    formData.locationId = item.value;
                    formData.locationName = item.label;
                  }
                "
                @change="
                  () => {
                    formData.locationId = '';
                    formData.locationName = '';
                  }
                "
              >
                <template slot-scope="{ item }">
                  <AutocompleteItem :item="item" />
                </template>
              </el-autocomplete>
            </FormItem>
          </el-col>
          <el-col :span="4">
            <el-form-item>
              <span slot="label">{{ $t('global.add') }}</span>
              <el-button
                class="width-100"
                type="primary"
                :disabled="
                  !formData.laboratory ||
                  formData.laboratory === '' ||
                  !formData.role ||
                  formData.role === '' ||
                  !formData.locationId ||
                  formData.locationId === ''
                "
                @click.prevent="addRole"
              >
                {{ $t('global.add') }}
              </el-button>
            </el-form-item>
          </el-col>
        </el-row>

        <el-row
          :gutter="20"
          type="flex"
        >
          <el-col :span="24">
            <div class="table-wrapper">
              <el-table :data="assignedRoles">
                <el-table-column
                  prop="laboratory"
                  :label="$t('users.create.laboratory')"
                  :formatter="
                    (row, column, cellValue) => $t(`constants.${cellValue}`)
                  "
                />
                <el-table-column
                  prop="name"
                  :label="$t('users.create.role')"
                />
                <el-table-column
                  prop="locationName"
                  :label="$t('users.create.location')"
                />
                <el-table-column
                  :label="$t('global.actions')"
                  width="100"
                >
                  <template slot-scope="scope">
                    <el-button
                      class="table-action-button"
                      icon="fas fa-times"
                      @click="removeRole(scope.row)"
                    />
                  </template>
                </el-table-column>
              </el-table>
            </div>
          </el-col>
        </el-row>

        <FormButtons>
          <el-button
            type="secondary"
            @click.prevent="abortUpdate"
          >
            {{ $t('users.create.abortUpdate') }}
          </el-button>
          <el-button
            type="primary"
            native-type="submit"
            :disabled="invalid || assignedRoles.length === 0"
            @click.prevent="handleSubmit(updateUser)"
          >
            {{ $t('users.update.updateUser') }}
          </el-button>
        </FormButtons>
      </el-form>
    </ValidationObserver>
  </el-dialog>
</template>

<script>
  import { usersApi, locationsManagementApi } from '@/api';
  import { laboratories } from '@/config/constants';
  import FormItem from '@/components/FormItem.vue';
  import ContentHeader from '@/components/ContentHeader.vue';
  import FormButtons from '@/components/FormButtons.vue';
  import AutocompleteItem from '@/components/AutocompleteItem.vue';

  const defaultData = {
    username: '',
    email: '',
    givenName: '',
    surname: '',
    laboratory: laboratories.CHIM,
    locationId: '',
    role: '',
    roles: [],
    locationName: '',
  };

  export default {
    name: 'EditUser',
    components: {
      FormItem,
      ContentHeader,
      FormButtons,
      AutocompleteItem,
    },
    props: {
      visible: { type: Boolean, default: false },
      initialData: { type: Object, default: () => defaultData },
      roles: { type: Array, default: () => [] },
    },
    data() {
      return {
        formData: { ...defaultData },
        assignedRoles: [],
        laboratories,
      };
    },
    watch: {
      initialData(data) {
        if (Object.keys(data).length > 0) {
          this.formData = { ...data };
          this.assignedRoles = data.roles.map((r) => ({
            laboratory: r.laboratory,
            name: r.name,
            locationId: r.locationId,
            locationName: r.locationId ? r.location.name : '',
          }));
        }
      },
    },
    methods: {
      async updateUser() {
        const { username, email, givenName, surname } = this.formData;
        const roles = this.assignedRoles.map((item) => ({
          laboratory: item.laboratory,
          locationId: item.locationId,
          name: item.name,
        }));
        try {
          await usersApi.patch(this.$route.params.userId, {
            username,
            email,
            givenName,
            surname,
            roles,
          });
          this.$notify({
            showClose: true,
            type: 'success',
            message: this.$t('users.update.success'),
          });
          this.formData = { ...defaultData };
          this.assignedRoles = [];
          this.$refs.userForm.reset();
          this.$emit('dialogClose', { success: true });
        } catch (error) {
          this.$notify({
            showClose: true,
            type: 'error',
            message: error.response.data.error.message,
          });
        }
      },
      abortUpdate() {
        this.formData = { ...defaultData };
        this.$refs.userForm.reset();
        this.assignedRoles = [];
        this.$emit('dialogClose', { success: false });
      },
      addRole() {
        const { laboratory, role, locationId, locationName } = this.formData;
        const isAlreadyAdded = this.assignedRoles.filter(
          (r) =>
            r.laboratory === laboratory &&
            r.name === role &&
            r.locationId === locationId
        );
        if (!isAlreadyAdded.length) {
          this.assignedRoles.push({
            laboratory,
            name: role,
            locationId,
            locationName,
          });
        } else {
          this.$notify({
            showClose: true,
            type: 'error',
            message: this.$t('users.create.roleExists'),
          });
        }
      },
      removeRole(row) {
        this.assignedRoles = this.assignedRoles.filter((r) => r !== row);
      },
      async locationsList(value, cb) {
        if (value && value.length >= 3) {
          const response = await locationsManagementApi.autocompleteFetch(
            'name',
            value,
            false
          );
          const locations = response.data.map((c) => ({
            label: c.name,
            value: c.id,
          }));
          cb(locations);
        } else {
          cb([]);
        }
      },
    },
  };
</script>
